import { useEffect, useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * usePriceStream — Round 84k Phase C scaffolding (Feb 15, 2026).
 *
 * Opens a Server-Sent Events connection to the backend's
 * `/api/prices/stream` endpoint with the given ticker subscription
 * list. Returns:
 *
 *   - `prices`: { [TICKER]: { price, change, ts } }
 *   - `status`: 'connecting' | 'connected' | 'dormant' | 'error' | 'idle'
 *               - 'dormant': SSE connected but the backend reports
 *                            MASSIVE_WEBSOCKET_ENABLED=false (so price
 *                            diffs won't flow — frontend should keep
 *                            using its existing polling fetches).
 *               - 'connected': SSE connected AND the backend WS is
 *                              live — frontend can trust the stream.
 *
 * The hook is intentionally CHEAP to mount on every page. Even with
 * the WebSocket dormant it consumes only a heartbeat every 15s. When
 * the user upgrades to the paid Massive plan, the same hook flips
 * from 'dormant' to 'connected' with zero code changes.
 *
 * `tickers` should be a stable array (memoize upstream) — passing a
 * fresh array every render will tear down and rebuild the SSE.
 */
export default function usePriceStream(tickers) {
  const [prices, setPrices] = useState({});
  const [status, setStatus] = useState('idle');

  // Round 93e (Jun 2026) — Derive a STABLE PRIMITIVE key from the ticker
  // array so the SSE doesn't tear down + rebuild on every parent render.
  // Previously the effect depended on `[tickers]`, which is a new array
  // reference each render (PortfolioSummary refetches owned-tickers
  // every 3s via the bump effect). React's shallow compare saw a "new"
  // dep, ran the cleanup (which reset lastSubKey + set status='idle'),
  // and then the inner de-dupe check `lastSubKey.current === key` never
  // hit — every tick tore the EventSource down and reopened it. The
  // <LivePriceIndicator> chip flashed off → connecting → live every
  // 3s, causing the "Positions" text next to the My Portfolio title
  // to slide left/right (visible especially on mobile). Depending on
  // a joined primitive string makes the effect re-run only when the
  // CONTENT changes.
  const tickersKey = (Array.isArray(tickers) ? tickers : [])
    .map((t) => String(t || '').toUpperCase())
    .filter(Boolean)
    .sort()
    .join(',');

  useEffect(() => {
    if (!tickersKey) {
      setStatus('idle');
      return;
    }
    setStatus('connecting');
    const url = `${API}/prices/stream?tickers=${encodeURIComponent(tickersKey)}`;
    const es = new EventSource(url, { withCredentials: true });

    es.addEventListener('status', (e) => {
      try {
        const data = JSON.parse(e.data || '{}');
        setStatus(data.enabled ? 'connected' : 'dormant');
      } catch {
        setStatus('connected');
      }
    });

    es.addEventListener('prices', (e) => {
      try {
        const diff = JSON.parse(e.data || '{}');
        setPrices((prev) => ({ ...prev, ...diff }));
      } catch { /* skip bad chunk */ }
    });

    es.addEventListener('error', () => {
      setStatus('error');
      // EventSource auto-retries; no manual reconnect needed.
    });

    return () => {
      es.close();
    };
  }, [tickersKey]);

  return { prices, status };
}
